import {
    AfterViewInit,
    Component,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    inject,
    input,
    viewChildren,
    TemplateRef,
    computed,
    signal,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Location, NgIf, NgFor, SlicePipe } from '@angular/common';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterLink,
} from '@angular/router';
import { MatDialog, MatDialogClose } from '@angular/material/dialog';
import { ReplaySubject, take, takeUntil } from 'rxjs';
import { MediaService, HideOnPrintDirective } from '@exl-ng/mulo-core';

import { LinkMetadataForFullAssetPage } from '../../../shared/interfaces/research-portal/full-asset-page/asset-model/general-helpers-model/link-metadata-for-full-asset-page.interface';
import { FileMetadataForFullAssetPage } from '../../../shared/interfaces/research-portal/full-asset-page/asset-model/general-helpers-model/file-metadata-for-full-asset-page.interface';
import { AssetPageService } from '../../asset-page.service';
import { ConfigurationHandlerService } from '../../../core/configuration-handler.service';
import { TranslateModule } from '@ngx-translate/core';
import { ArcviewContainerComponent } from '../../../parts/arcview/arcview-container/arcview-container.component';
import {
    MatAnchor,
    MatMiniFabButton,
    MatButton,
} from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
    FileListComponent,
    FileListItemComponent,
    FileActionDirective,
    LoadingSlateComponent,
} from '@exl-ng/mulo-common';

@Component({
    selector: 'esp-asset-files-and-links',
    templateUrl: './asset-files-and-links.component.html',
    styleUrls: ['./asset-files-and-links.component.scss'],
    imports: [
        NgIf,
        FileListComponent,
        NgFor,
        FileListItemComponent,
        FileActionDirective,
        MatTooltip,
        MatIcon,
        MatAnchor,
        RouterLink,
        MatMiniFabButton,
        MatDialogClose,
        ArcviewContainerComponent,
        MatButton,
        HideOnPrintDirective,
        LoadingSlateComponent,
        SlicePipe,
        TranslateModule,
    ],
    host: {
        '[class.is-expanded]': 'showAll()',
    },
})
export class AssetFilesAndLinksComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private assetPageService = inject(AssetPageService);
    private dialogRef = inject(MatDialog);
    private sanitizer = inject(DomSanitizer);
    route = inject(ActivatedRoute);
    private router = inject(Router);
    private location = inject(Location);
    private media = inject(MediaService);
    private config = inject(ConfigurationHandlerService);

    readonly mmsID = input<string>(undefined);
    readonly files = input<FileMetadataForFullAssetPage[]>([]);
    readonly links = input<LinkMetadataForFullAssetPage[]>([]);
    readonly openURLLink = input<string>(undefined);
    readonly iframeTemplates = viewChildren<TemplateRef<any>>('iframeTemplate');
    _filesAndLinks = [];
    _onDestroy = new ReplaySubject<void>();
    useArcview = false;
    _dialogOpen = false;

    openURL = computed(() => {
        const url = new URL(this.openURLLink());
        return this.sanitizer.bypassSecurityTrustResourceUrl(url.toString());
    });
    iframeLoading = signal(true);
    iframeHeight = signal(150);
    maxVisible = signal(5);
    showAll = signal(false);

    ngOnInit(): void {
        this.assetPageService
            .getViewer(this.mmsID())
            .pipe(take(1))
            .subscribe((res: any) => (this._filesAndLinks = res?.files));

        this.useArcview = this.config.isCustomerParameterEnabled(
            'esploro_enable_new_viewer',
        );
    }

    ngAfterViewInit() {
        this.router.events.pipe(takeUntil(this._onDestroy)).subscribe((e) => {
            if (e instanceof NavigationEnd) {
                this.openViewer(this.route.snapshot.fragment);
            }
        });
        // router.events does not emit on initial load
        this.openViewer(this.route.snapshot.fragment);
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    handleIframeLoad(event) {
        this.iframeLoading.set(false);

        // for future debugging: contentDocument is null if url is not same origin
        // see: https://developer.mozilla.org/en-US/docs/Web/API/HTMLIFrameElement/contentDocument
        const contentHeight = event.target.contentDocument?.body?.scrollHeight;
        this.iframeHeight.update((prev) => contentHeight || prev);
    }

    updateDownloadFileStatistics(file) {
        this.updateFilesStatistics(file, 'DOWNLOAD');
    }

    updateViewFileStatistics(file) {
        this.updateFilesStatistics(file, 'VIEW');
    }

    updateFilesStatistics(downloadedFile, action: string) {
        const params = {
            repId: downloadedFile.esploroFileModel.repId,
            mmsId: downloadedFile.esploroFileModel.mmsId,
            pid: downloadedFile.esploroFileModel.mid,
        };
        this.assetPageService.updateStatistics(params, action).subscribe();
    }

    isSupplementalFile(file: FileMetadataForFullAssetPage) {
        if (file.esploroFileModel.fileSupplementalEsploro === 'yes') {
            return true;
        }
        return false;
    }

    openViewer(fragment = '') {
        const urlWithFragment = this.router
            .createUrlTree(['.'], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve',
                fragment,
            })
            .toString();
        const urlWithoutFragment = this.router
            .createUrlTree(['.'], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve',
            })
            .toString();

        const fileIdx = Number(fragment?.replace('file-', ''));
        const linkIdx = fileIdx - (this.files()?.length || 0);
        const viewerLink =
            this.files()[fileIdx]?.viewerLink ||
            this.links()[linkIdx]?.viewerLink;
        const shouldOpen = !this._dialogOpen || !this.useArcview;

        if (fragment?.startsWith('file-') && viewerLink && shouldOpen) {
            if (this.media.is('gt-sm')) {
                // standard desktop behavior
                const template = this.iframeTemplates()[fileIdx];
                this._dialogOpen = true;
                this.dialogRef
                    .open(template, {
                        panelClass: 'esp-viewer',
                        ariaLabel: this.assetPageService.getTranslation(
                            'research.portal.fulldisplay.file.viewerDialog',
                        ),
                    })
                    .afterClosed()
                    .pipe(takeUntil(this._onDestroy))
                    .subscribe(() => {
                        this._dialogOpen = false;
                        this.location.replaceState(urlWithoutFragment);
                    });
            } else {
                this.location.replaceState(urlWithoutFragment);
                if (this.useArcview) {
                    const arcviewStandalone = this.router.createUrlTree(
                        ['filesAndLinks'],
                        {
                            relativeTo: this.route,
                            queryParamsHandling: 'merge',
                            queryParams: { index: fileIdx },
                        },
                    );
                    window.open('.' + arcviewStandalone, '_blank');
                } else {
                    window.open(viewerLink, '_blank');
                }
            }
        }
    }

    getViewerLink(
        item: FileMetadataForFullAssetPage & LinkMetadataForFullAssetPage,
    ) {
        if (item?.viewerLink) {
            const viewerCount = [...this.files(), ...this.links()].filter(
                (_) => _.viewerLink != null,
            ).length;
            const viewLinkUrl = new URL(item.viewerLink);
            viewLinkUrl.searchParams.set(
                'mode',
                viewerCount > 1 ? 'embedded' : 'quickaccess',
            );
            return this.sanitizer.bypassSecurityTrustResourceUrl(
                viewLinkUrl.toString(),
            );
        } else if (item?.esploroAssetLinkModel) {
            return item.esploroAssetLinkModel.linkURL;
        } else {
            return null;
        }
    }

    getFileContentTypeText(file: FileMetadataForFullAssetPage) {
        let result: string = null;
        if (file.fileTypeLabel) {
            result = file.fileTypeLabel;
            if (this.isSupplementalFile(file)) {
                result += ` (${this.assetPageService.getTranslation(
                    'research.portal.fulldisplay.file.supplemental',
                )})`;
            }
        } else if (this.isSupplementalFile(file)) {
            result = this.assetPageService.getTranslation(
                'research.portal.fulldisplay.file.supplemental',
            );
        }
        return result;
    }

    getFileAccessRightText(file: FileMetadataForFullAssetPage) {
        let result: string = file.accessRightStatement;
        if (file.embargoExpiryDate) {
            result += `, ${this.assetPageService.getTranslation(
                'research.portal.fulldisplay.file.embargoDate.expiryDate',
            )} ${file.embargoExpiryDate}`;
        }
        if (file.accessRightDeniedNote) {
            result += `, ${file.accessRightDeniedNote}`;
        }
        return result;
    }

    getLinkTitle(link: LinkMetadataForFullAssetPage) {
        if (link?.esploroAssetLinkModel?.linkTitle) {
            return link.esploroAssetLinkModel.linkTitle;
        }
        return link?.esploroAssetLinkModel?.linkURL;
    }

    getLinkURL(link: LinkMetadataForFullAssetPage) {
        if (link?.viewerLink) {
            return link.viewerLink;
        }
        return link.esploroAssetLinkModel.linkURL;
    }

    getHasMetadata(
        item: FileMetadataForFullAssetPage & LinkMetadataForFullAssetPage,
    ) {
        let hasType;
        let hasDesc;
        if (item?.esploroFileModel) {
            hasType =
                !!item.esploroFileModel?.fileTypeEsploroCode ||
                this.isSupplementalFile(item);
            hasDesc = !!item.esploroFileModel?.fileDescriptionEsploro;
        } else {
            hasType =
                !!item?.esploroAssetLinkModel?.linkType ||
                !!item?.esploroAssetLinkModel?.linkSupplemental;
            hasDesc = !!item?.esploroAssetLinkModel?.linkDescription;
        }
        const hasLicense = !!item?.licenseCode;
        const hasAccessRights =
            !!item?.esploroAssetLinkModel?.linkRights ||
            !!item?.accessRightStatement;

        return hasType || hasDesc || hasLicense || hasAccessRights;
    }

    getLinkContentTypeText(link: LinkMetadataForFullAssetPage) {
        let result: string = null;
        if (link.linkTypeLabel) {
            result = link.linkTypeLabel;
            if (link.esploroAssetLinkModel.linkSupplemental) {
                result += ` (${this.assetPageService.getTranslation(
                    'research.portal.fulldisplay.file.supplemental',
                )})`;
            }
        } else if (link.esploroAssetLinkModel.linkSupplemental) {
            result = this.assetPageService.getTranslation(
                'research.portal.fulldisplay.file.supplemental',
            );
        }
        return result;
    }

    handleArcviewIdxChange(index) {
        const file = this.files()[index];
        if (!file.haveAccessRight) {
            this.updateDownloadFileStatistics(file);
        } else {
            this.updateFilesStatistics(file, 'FILE_ACCESS_DENIED');
        }
        this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve',
            fragment: 'file-' + index,
            replaceUrl: true,
        });
    }
}
